// useFetchData.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const useFetchDatav2 = () => {
   const [data, setData] = useState(null);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState("");
   const navigate = useNavigate();

   const fetchData = async (url, method = "GET", body = null, contentType = "application/json", responseType = "json", signal = null) => {
      if (!navigator.onLine) {
         setError("Network connection is lost");
         return { responseData: null, fetchLoading: false, fetchError: "Network connection is lost" };
      }

      setLoading(true);

      const requestOptions = {
         method,
         headers: {},
         body: null,
         credentials: "include",
         ...(signal && { signal }),
      };

      // Set headers and body based on content type
      if (method === "POST") {
         if (body instanceof FormData) {
            requestOptions.body = body; // Pass FormData directly
            // Do NOT set 'Content-Type' header; the browser will automatically handle it.
         } else if (contentType === "application/json") {
            requestOptions.body = JSON.stringify(body); // For JSON payloads
            requestOptions.headers["Content-Type"] = "application/json";
         } else if (contentType === "application/x-www-form-urlencoded") {
            requestOptions.body = new URLSearchParams(body); // Convert to URL-encoded string
            requestOptions.headers["Content-Type"] = "application/x-www-form-urlencoded";
         }
      }

      try {
         // if we pass a url without the / we add it here
         //const fullUrl = url.startsWith("http") ? url : `${API_BASE.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
         const response = await fetch(url, requestOptions);
         if (!response.ok) {
            throw new Error(`Failed to fetch data: ${response.statusText}`);
         }

         //const result = await response.json();
         let result;
         if (responseType === "json") {
            result = await response.json();
         } else if (responseType === "blob") {
            result = await response.blob(); // Handle binary data like PDFs
         } else {
            result = await response.text(); // Default text response
         }

         // Check for session expiration
         if (result.st === -2) {
            // Redirect to sign-in page if session expired
            navigate("/pages/signin3", { replace: true });
            return { responseData: null, fetchLoading: false, fetchError: "Session expired" };
         }

         //setData(result);
         return { responseData: result, fetchLoading: false, fetchError: null };
      } catch (error) {
         if (error.name === "AbortError") {
            console.warn("Fetch request aborted:", error); // Log for debugging
            return { responseData: null, fetchLoading: false, fetchError: "Fetch aborted" };
         } else {
            setError(error.message);
            return { responseData: null, fetchLoading: false, fetchError: error.message };
         }
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      const handleConnectionChange = () => {
         if (!navigator.onLine) {
            setError("Network connection is lost");
         }
      };

      window.addEventListener("offline", handleConnectionChange);
      window.addEventListener("online", handleConnectionChange);

      return () => {
         window.removeEventListener("offline", handleConnectionChange);
         window.removeEventListener("online", handleConnectionChange);
      };
   }, []);

   return { data, loading, error, fetchData };
};

export default useFetchDatav2;
